import {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureRunDataEntryMode, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  BaseCondition,
  Condition,
  conditionsForEveryMethod,
  ConditionGroup,
  useConditionsAndExperimentalInputs,
  useExperimentalInputsContextProperties,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import {StudyDesignTabEditState} from '../design-tabs/StudyDesignTabSavingButtons';
import {
  MethodExecutionLimitState,
  useMethodExecutionLimitState,
} from '../execution-status/useMethodExecutionLimitState';
import {ExperimentFlowchart, LargestTableTypeSizes} from './ExperimentalInputs';

/**
 * Represents the flowchart version with required properties for study design.
 */
export interface StudyDesignFlowchartVersion {
  id: string;
  largestTableTypeSizes: ReadonlyArray<{tableKind: string; size: number}>;
}

/**
 * Parameters for the useStudyDesignState hook.
 */
export interface UseStudyDesignStateParams<TCondition extends BaseCondition> {
  /** Whether this holds a procedure run or a procedure run plan */
  isPlan: boolean;
  /** Initial experimental inputs extracted from procedure run or plan */
  initialExperimentalInputs: ExperimentalInputs;
  /** Template flowchart with method nodes */
  templateFlowchart: ExperimentFlowchart;
  /** Persisted conditions loaded from the procedure run or plan. */
  initialPersistedConditions: Array<TCondition>;
  /**
   * Factory for the fallback default conditions, used when stored conditions are missing or out of
   * sync with the current procedure. Must produce values matching `TCondition` (e.g.
   * {@link createDefaultConditionGroups}).
   */
  createDefaultConditions: (procedureType: ProcedureType) => Array<TCondition>;
  /** Procedure type (RECIPE or ASSAY) */
  procedureType: ProcedureType;
  /** Flowchart version with table size info */
  flowchartVersion: StudyDesignFlowchartVersion;
  /** Data entry mode for the run/plan */
  dataEntryMode: ProcedureRunDataEntryMode;
  /** Procedure ID */
  procedureId: string;
  /** Whether the design tab should initialize in edit mode */
  initialEditMode?: boolean;
  /**
   * Whether to select the first method on mount. The legacy design editor relies on a method being
   * selected synchronously on open. When `false`, the hook starts with no method selected
   * (`selectedMethodId === null`). Defaults to `true` to preserve legacy behavior.
   */
  selectFirstMethodInitially?: boolean;
  /** Whether archived condition columns appear in input tables. */
  showArchivedConditions?: boolean;
}

/**
 * Result of the useStudyDesignState hook.
 */
export interface UseStudyDesignStateResult<TCondition extends BaseCondition> {
  // UI state
  selectedMethodId: string | null;
  setSelectedMethodId: (id: string | null) => void;
  editState: StudyDesignTabEditState;
  setEditState: (state: StudyDesignTabEditState) => void;
  selectedDesignTabIndex: number | null;
  setSelectedDesignTabIndex: (index: number | null) => void;
  sidepanelName: string;
  setSidepanelName: (name: string) => void;

  // Conditions and inputs
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (inputs: ExperimentalInputs) => void;
  resetConditionsAndInputs: (conditions: Array<TCondition>, experimentalInputs: ExperimentalInputs) => void;

  // Derived state
  context: ExperimentalInputsContextProperties;
  methodExecutionLimitState: MethodExecutionLimitState;
  templateFlowchart: ExperimentFlowchart;
}

const PARAMETER_VALUES_TAB_NAME = 'Conditions';

/**
 * Hook that encapsulates shared state management for study design tabs.
 * Used by both ProcedureRunDesignTabContents and ProcedureRunPlanDesignTabContents.
 */
export function useStudyDesignState<TCondition extends Condition | ConditionGroup>(
  params: UseStudyDesignStateParams<TCondition>
): UseStudyDesignStateResult<TCondition> {
  const {
    initialExperimentalInputs,
    templateFlowchart,
    initialPersistedConditions,
    createDefaultConditions,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
    isPlan,
    initialEditMode = false,
    selectFirstMethodInitially = true,
    showArchivedConditions,
  } = params;

  // UI state
  const isEmptyFlowchart = templateFlowchart.nodes.length === 0;
  const [storedMethodId, setSelectedMethodId] = useState<string | null>(
    selectFirstMethodInitially && !isEmptyFlowchart ? templateFlowchart.nodes[0].id : null
  );
  // If the stored method ID no longer exists in the current flowchart (e.g. the component stayed
  // mounted while the procedureRun prop changed to a different run via Apollo cache hit), reset to
  // the first node, or null if the flowchart is now empty.
  let selectedMethodId = storedMethodId;
  if (selectedMethodId != null && !templateFlowchart.nodes.some((n) => n.id === selectedMethodId)) {
    selectedMethodId = templateFlowchart.nodes[0]?.id ?? null;
  }
  if (selectedMethodId !== storedMethodId) {
    // NOTE: While setting state on render has the potential to cause infinite render loops, we do not
    // expect this to happen: the next selectedMethodId is always either a node in
    // templateFlowchart.nodes or null.
    setSelectedMethodId(selectedMethodId);
  }
  // Use useStateIfMounted because setEditState may be called in async callbacks
  const [editState, setEditState] = useStateIfMounted(
    initialEditMode ? StudyDesignTabEditState.EDITING : StudyDesignTabEditState.SAVED
  );
  const [selectedDesignTabIndex, setSelectedDesignTabIndex] = useState<number | null>(0);
  const [sidepanelName, setSidepanelName] = useState<string>(PARAMETER_VALUES_TAB_NAME);

  const procedure = useMemo(
    () => ({
      id: procedureId,
      procedureType,
      methods: templateFlowchart.nodes,
    }),
    [procedureId, procedureType, templateFlowchart.nodes]
  );
  // Conditions and experimental inputs state
  const {conditions, setConditions, experimentalInputs, setExperimentalInputs, resetConditionsAndInputs} =
    useConditionsAndExperimentalInputs<TCondition>(
      procedure,
      createDefaultConditions,
      procedureId,
      initialPersistedConditions,
      initialExperimentalInputs
    );

  // useConditionsAndExperimentalInputs returns null when procedure ID is null,
  // which should never be the case when using this hook
  assert(
    conditions != null && experimentalInputs != null,
    'Expected conditions and experimentalInputs to be non-null'
  );

  // This shared-row state model maps its condition list to every flowchart method.
  const conditionsByMethodId = useMemo(
    () => conditionsForEveryMethod(templateFlowchart, conditions),
    [templateFlowchart, conditions]
  );

  // Context for ExperimentalInputs components
  const context = useExperimentalInputsContextProperties(
    conditionsByMethodId,
    experimentalInputs,
    setExperimentalInputs,
    templateFlowchart,
    procedureType,
    // Should be locked when not in editing state
    editState !== StudyDesignTabEditState.EDITING,
    isPlan,
    showArchivedConditions
  );
  assert(context != null, 'Expected context to be non-null');

  // Method execution limit state
  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditions,
    flowchartVersion.largestTableTypeSizes as LargestTableTypeSizes,
    dataEntryMode
  );

  return {
    // UI state
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,

    // Conditions and inputs
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    resetConditionsAndInputs,

    // Derived state
    context,
    methodExecutionLimitState,
    templateFlowchart,
  };
}
